import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaHeartbeat, FaPhoneAlt, FaWhatsapp, FaBars, FaTimes, FaSun, FaMoon } from "react-icons/fa";
import { COMPANY, NAV_LINKS } from "../data/content";
import { useTheme } from "./ThemeProvider";

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { theme, toggle } = useTheme();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goto = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "glass-strong shadow-md py-2"
                    : "bg-transparent py-4"
            }`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                {/* Logo */}
                <button
                    data-testid="header-logo"
                    onClick={() => goto("home")}
                    className="flex items-center gap-2.5 group"
                >
                    <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-brand-700 to-brand-400 flex items-center justify-center shadow-lg group-hover:shadow-brand-500/40 transition-all">
                        <FaHeartbeat className="text-white text-xl" />
                        <div className="absolute -inset-1 rounded-xl bg-brand-500/20 blur opacity-0 group-hover:opacity-100 transition" />
                    </div>
                    <div className="text-left leading-tight">
                        <div className="font-heading font-extrabold text-base sm:text-lg text-slate-900 dark:text-white uppercase">EZHIL <span className="text-brand-600 dark:text-brand-300">BIOMEDICAL</span></div>
                        <div className="text-[10px] sm:text-[11px] tracking-[0.18em] text-slate-500 dark:text-slate-400 uppercase">Sales &amp; Services</div>
                    </div>
                </button>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((link) => (
                        <button
                            key={link.id}
                            data-testid={`nav-link-${link.id}`}
                            onClick={() => goto(link.id)}
                            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-brand-600 dark:hover:text-brand-300 rounded-lg hover:bg-brand-50 dark:hover:bg-slate-800/60 transition-all"
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* CTAs */}
                <div className="flex items-center gap-2">
                    <button
                        data-testid="theme-toggle-btn"
                        onClick={toggle}
                        aria-label="Toggle theme"
                        className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-brand-50 dark:hover:bg-slate-700 transition"
                    >
                        {theme === "light" ? <FaMoon className="text-sm" /> : <FaSun className="text-sm" />}
                    </button>
                    <a
                        href={`tel:${COMPANY.phoneDigits}`}
                        data-testid="header-call-btn"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-slate-800 dark:text-brand-300 dark:hover:bg-slate-700 transition"
                    >
                        <FaPhoneAlt className="text-xs" /> Call Now
                    </a>
                    <a
                        href={`https://wa.me/${COMPANY.phoneDigits}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid="header-whatsapp-btn"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                    >
                        <FaWhatsapp /> WhatsApp
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                        aria-label="Menu"
                    >
                        {open ? <FaTimes /> : <FaBars />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden glass-strong border-t border-slate-200 dark:border-slate-800"
                    >
                        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
                            {NAV_LINKS.map((link) => (
                                <button
                                    key={link.id}
                                    data-testid={`mobile-nav-${link.id}`}
                                    onClick={() => goto(link.id)}
                                    className="text-left px-4 py-3 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-brand-50 dark:hover:bg-slate-800 font-medium"
                                >
                                    {link.label}
                                </button>
                            ))}
                            <div className="flex gap-2 pt-2">
                                <a href={`tel:${COMPANY.phoneDigits}`} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold bg-brand-700 text-white"><FaPhoneAlt /> Call</a>
                                <a href={`https://wa.me/${COMPANY.phoneDigits}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold bg-emerald-500 text-white"><FaWhatsapp /> WhatsApp</a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
